import { RECAPTCHA_SITE_KEY } from '../../../config';
import { throwIfAborted } from '../../../utils/abortSignal';
import { isCaptchaGloballyEnabled } from './captcha';

export const FEEDBACK_SEND_RECAPTCHA_ACTION = 'feedback_send';

const RECAPTCHA_SCRIPT_ID = 'psn-recaptcha-enterprise-script';
const RECAPTCHA_SCRIPT_BASE_URL = 'https://www.recaptcha.net/recaptcha/enterprise.js';
const RECAPTCHA_SCRIPT_LOAD_STATE_LOADING = 'loading';
const RECAPTCHA_SCRIPT_LOAD_STATE_LOADED = 'loaded';
const RECAPTCHA_SCRIPT_LOAD_STATE_ERROR = 'error';

let scriptLoadPromise = null;

const getEnterpriseRecaptcha = () => window.grecaptcha?.enterprise;

const hasEnterpriseApi = () => {
  const enterprise = getEnterpriseRecaptcha();
  return !!enterprise && typeof enterprise.ready === 'function' && typeof enterprise.execute === 'function';
};

const getConfiguredSiteKey = () => RECAPTCHA_SITE_KEY?.trim() || null;

const getScriptUrl = (siteKey, scriptBaseUrl = RECAPTCHA_SCRIPT_BASE_URL) =>
  `${scriptBaseUrl}?render=${encodeURIComponent(siteKey)}`;

const getManagedRecaptchaScript = () => document.getElementById(RECAPTCHA_SCRIPT_ID);

const getAllRecaptchaScripts = () =>
  Array.from(
    document.querySelectorAll(
      'script[src*="google.com/recaptcha/enterprise.js"], script[src*="recaptcha.net/recaptcha/enterprise.js"]',
    ),
  );

const getRecaptchaScriptSiteKey = (scriptEl) => {
  if (!scriptEl?.src) {
    return null;
  }

  if (scriptEl.dataset?.psnRecaptchaSiteKey) {
    return scriptEl.dataset.psnRecaptchaSiteKey;
  }

  try {
    const url = new URL(scriptEl.src, window.location.href);
    return url.searchParams.get('render') || url.searchParams.get('siteKey');
  } catch {
    return null;
  }
};

const setManagedRecaptchaScriptMetadata = (scriptEl, siteKey, loadState) => {
  if (!scriptEl) {
    return;
  }

  scriptEl.dataset.psnRecaptchaSiteKey = siteKey;
  scriptEl.dataset.psnRecaptchaLoadState = loadState;
};

const getManagedRecaptchaScriptLoadState = (scriptEl = getManagedRecaptchaScript()) =>
  scriptEl?.dataset?.psnRecaptchaLoadState || null;

const isManagedRecaptchaScriptForSiteKey = (siteKey, scriptEl = getManagedRecaptchaScript()) =>
  !!scriptEl && scriptEl.id === RECAPTCHA_SCRIPT_ID && getRecaptchaScriptSiteKey(scriptEl) === siteKey;

const isManagedRecaptchaReady = (siteKey) => {
  const scriptEl = getManagedRecaptchaScript();
  if (!isManagedRecaptchaScriptForSiteKey(siteKey, scriptEl)) {
    return false;
  }

  const loadState = getManagedRecaptchaScriptLoadState(scriptEl);
  if (loadState === RECAPTCHA_SCRIPT_LOAD_STATE_ERROR) {
    return false;
  }

  return hasEnterpriseApi() && (loadState === RECAPTCHA_SCRIPT_LOAD_STATE_LOADED || loadState === null);
};

const markRecaptchaRuntimeFailure = (error) => {
  if (error && typeof error === 'object') {
    error.isRecaptchaRuntimeFailure = true;
  }

  return error;
};

export const isRecaptchaRuntimeFailure = (error) => error?.isRecaptchaRuntimeFailure === true;

const removeManagedRecaptchaScript = () => {
  const scriptEl = getManagedRecaptchaScript();
  if (scriptEl) {
    scriptEl.remove();
  }
};

const waitForEnterpriseApi = (timeoutMs = 5000) =>
  new Promise((resolve, reject) => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      if (hasEnterpriseApi()) {
        clearInterval(interval);
        resolve();
        return;
      }

      if (Date.now() - startTime >= timeoutMs) {
        clearInterval(interval);
        const availableGreCaptchaKeys =
          window.grecaptcha && typeof window.grecaptcha === 'object'
            ? Object.keys(window.grecaptcha).join(', ')
            : 'none';
        reject(
          new Error(
            `Timed out while waiting for reCAPTCHA Enterprise API. window.grecaptcha keys: ${availableGreCaptchaKeys}`,
          ),
        );
      }
    }, 50);
  });

const loadRecaptchaScriptFromUrl = (siteKey, scriptUrl) =>
  new Promise((resolve, reject) => {
    let completed = false;
    let scriptEl = null;
    const allRecaptchaScripts = getAllRecaptchaScripts();

    const removeScriptListeners = () => {
      if (!scriptEl) {
        return;
      }

      scriptEl.removeEventListener('load', onLoad);
      scriptEl.removeEventListener('error', onError);
      if (scriptEl.onload === onLoad) {
        scriptEl.onload = null;
      }
      if (scriptEl.onerror === onError) {
        scriptEl.onerror = null;
      }
    };

    const complete = (callback, value) => {
      if (completed) {
        return;
      }

      completed = true;
      removeScriptListeners();
      callback(value);
    };

    const onLoad = () => {
      setManagedRecaptchaScriptMetadata(scriptEl, siteKey, RECAPTCHA_SCRIPT_LOAD_STATE_LOADED);
      waitForEnterpriseApi()
        .then(() => complete(resolve))
        .catch((error) => complete(reject, error));
    };

    const onError = () => {
      setManagedRecaptchaScriptMetadata(scriptEl, siteKey, RECAPTCHA_SCRIPT_LOAD_STATE_ERROR);
      complete(reject, new Error(`Failed to load reCAPTCHA Enterprise script from ${scriptUrl}.`));
    };

    scriptEl =
      allRecaptchaScripts.find((candidate) => candidate.id === RECAPTCHA_SCRIPT_ID) || getManagedRecaptchaScript();
    if (
      scriptEl &&
      (scriptEl.src !== scriptUrl ||
        !isManagedRecaptchaScriptForSiteKey(siteKey, scriptEl) ||
        getManagedRecaptchaScriptLoadState(scriptEl) === RECAPTCHA_SCRIPT_LOAD_STATE_ERROR)
    ) {
      scriptEl.remove();
      scriptEl = null;
    }

    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.id = RECAPTCHA_SCRIPT_ID;
      scriptEl.src = scriptUrl;
      scriptEl.async = true;
      scriptEl.defer = true;
      setManagedRecaptchaScriptMetadata(scriptEl, siteKey, RECAPTCHA_SCRIPT_LOAD_STATE_LOADING);
      scriptEl.onload = onLoad;
      scriptEl.onerror = onError;
      document.head.appendChild(scriptEl);
      return;
    }

    if (isManagedRecaptchaReady(siteKey)) {
      complete(resolve);
      return;
    }

    scriptEl.addEventListener('load', onLoad, { once: true });
    scriptEl.addEventListener('error', onError, { once: true });

    const managedScriptLoadState = getManagedRecaptchaScriptLoadState(scriptEl);
    if (
      managedScriptLoadState === RECAPTCHA_SCRIPT_LOAD_STATE_LOADED ||
      (managedScriptLoadState === null && isManagedRecaptchaScriptForSiteKey(siteKey, scriptEl) && hasEnterpriseApi())
    ) {
      onLoad();
    }
  });

const loadRecaptchaScript = (siteKey) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(
      markRecaptchaRuntimeFailure(new Error('reCAPTCHA can only be loaded in browser environments.')),
    );
  }

  if (isManagedRecaptchaReady(siteKey)) {
    return Promise.resolve();
  }

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  const scriptUrl = getScriptUrl(siteKey);

  scriptLoadPromise = loadRecaptchaScriptFromUrl(siteKey, scriptUrl).catch((error) => {
    removeManagedRecaptchaScript();
    scriptLoadPromise = null;
    throw markRecaptchaRuntimeFailure(error);
  });

  return scriptLoadPromise;
};

export const warmUpRecaptcha = () => {
  if (!isCaptchaGloballyEnabled()) {
    return Promise.resolve(null);
  }

  const siteKey = getConfiguredSiteKey();
  if (!siteKey) {
    return Promise.reject(
      markRecaptchaRuntimeFailure(
        new Error('Captcha is enabled but RECAPTCHA_SITE_KEY is missing for the reCAPTCHA provider.'),
      ),
    );
  }

  return loadRecaptchaScript(siteKey);
};

export const getRecaptchaToken = async (
  action = FEEDBACK_SEND_RECAPTCHA_ACTION,
  { required = isCaptchaGloballyEnabled(), signal } = {},
) => {
  throwIfAborted(signal);

  if (!required) {
    return null;
  }

  const siteKey = getConfiguredSiteKey();
  if (!siteKey) {
    throw markRecaptchaRuntimeFailure(
      new Error('Captcha is enabled but RECAPTCHA_SITE_KEY is missing for the reCAPTCHA provider.'),
    );
  }

  let phase = 'script-load';

  try {
    await loadRecaptchaScript(siteKey);
    throwIfAborted(signal);

    phase = 'enterprise-api-ready';
    await waitForEnterpriseApi();
    throwIfAborted(signal);

    const recaptcha = getEnterpriseRecaptcha();
    if (!hasEnterpriseApi()) {
      throw new Error('reCAPTCHA Enterprise API is unavailable.');
    }

    phase = 'ready-callback';
    await new Promise((resolve, reject) => {
      try {
        recaptcha.ready(resolve);
      } catch (error) {
        reject(error);
      }
    });
    throwIfAborted(signal);

    phase = 'execute';
    const token = await recaptcha.execute(siteKey, { action });
    throwIfAborted(signal);
    if (!token || typeof token !== 'string') {
      throw new Error('reCAPTCHA Enterprise token generation failed.');
    }

    return token;
  } catch (error) {
    throw markRecaptchaRuntimeFailure(error);
  }
};
